import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Star, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function FeedbackResponsesPanel() {
  const qc = useQueryClient();
  const [sentiment, setSentiment] = useState("");
  const [running, setRunning] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-feedback", sentiment],
    queryFn: () => api.get("/admin/feedback-responses", { params: { sentiment } }).then((r) => r.data),
  });
  const stats = data?.stats || {};

  const runNow = async () => {
    setRunning(true);
    try {
      const { data: r } = await api.post("/admin/feedback-surveys/run");
      toast.success(`Survey sweep complete — ${r.sent} thank-you email${r.sent === 1 ? "" : "s"} sent.`);
      qc.invalidateQueries({ queryKey: ["admin-feedback"] });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  return (
    <div data-testid="feedback-responses-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="font-serif text-2xl text-[#1A242B]">Feedback surveys</h2>
          <p className="text-sm text-[#4A5D6B]">Post-visit thank-you surveys. 4-5★ responders get a Google-review prompt; 1-3★ route here privately for follow-up.</p>
        </div>
        <button onClick={runNow} disabled={running} data-testid="feedback-run-now" className="eyw-btn-ghost !py-2 text-sm disabled:opacity-60">{running ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />} Run sweep now</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[["Surveys sent", stats.sent], ["Responses", stats.responded], ["Positive (4-5★)", stats.positive], ["Needs follow-up", stats.negative]].map(([l, v], i) => (
          <div key={l} className="eyw-card p-4 text-center" data-testid={`feedback-stat-${i}`}>
            <p className={`text-2xl font-serif ${i === 3 && v > 0 ? "text-[#B5794E]" : "text-[#144F75]"}`}>{v ?? 0}</p>
            <p className="text-xs text-[#8FA89B] mt-0.5">{l}</p>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mb-4">
        {[["", "All"], ["negative", "Needs follow-up"], ["positive", "Positive"]].map(([v, l]) => (
          <button key={v} onClick={() => setSentiment(v)} data-testid={`feedback-filter-${v || "all"}`}
            className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${sentiment === v ? "bg-[#144F75] text-white border-[#144F75]" : "border-[#E5E0D8] text-[#4A5D6B] hover:border-[#144F75]/40"}`}>{l}</button>
        ))}
      </div>
      {isLoading ? <p className="text-sm text-[#4A5D6B] flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Loading…</p> : (
        <div className="space-y-3">
          {(data?.responses || []).map((r) => (
            <div key={r.id} className={`eyw-card p-5 ${r.sentiment === "negative" ? "border-[#D98E73]/50 bg-[#D98E73]/[0.04]" : ""}`} data-testid={`feedback-row-${r.id}`}>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex">{[1, 2, 3, 4, 5].map((n) => <Star key={n} size={14} className={n <= (r.response?.experience || 0) ? "fill-[#D98E73] text-[#D98E73]" : "text-[#E5E0D8]"} />)}</div>
                <p className="text-sm font-medium text-[#1A242B]">{r.name || r.email}</p>
                <p className="text-xs text-[#8FA89B]">{r.subject_title}{r.enhancer_name ? ` · ${r.enhancer_name}` : ""} · {String(r.responded_at || "").slice(0, 10)}</p>
                {r.testimonial_id && <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#5C8A72]/15 text-[#5C8A72]">testimonial submitted</span>}
              </div>
              {r.response?.comments && <p className="text-sm text-[#4A5D6B] mt-2 italic">"{r.response.comments}"</p>}
              {r.response?.return_likelihood > 0 && <p className="text-xs text-[#8FA89B] mt-1">Likelihood to return: {r.response.return_likelihood}/5</p>}
              {r.sentiment === "negative" && <p className="text-xs text-[#B5794E] mt-2">Routed privately — follow up at <a className="underline" href={`mailto:${r.email}`}>{r.email}</a>. No public review was requested.</p>}
            </div>
          ))}
          {(data?.responses || []).length === 0 && <p className="text-sm text-[#8FA89B] py-8 text-center" data-testid="feedback-empty">No responses yet — they'll appear as members reply to post-visit surveys.</p>}
        </div>
      )}
    </div>
  );
}
